import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { ProjectCard } from '@/components/projects/project-card'

interface ProjectGridProps {
    projects: Array<ProjectSummary>
}

interface FilterButtonProps {
    label: string
    count: number
    active: boolean
    onSelect: () => void
}

const FilterButton: FC<FilterButtonProps> = ({ label, count, active, onSelect }): ReactNode => {
    return (
        <button
            type='button'
            aria-pressed={active}
            onClick={onSelect}
            className={cn(
                'inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-foreground/80 hover:border-foreground/30 hover:text-foreground'
            )}
        >
            {label}

            <span className='font-mono text-xs opacity-60'>
                {count}
            </span>
        </button>
    )
}

export const ProjectGrid: FC<ProjectGridProps> = ({ projects }): ReactNode => {
    const [activeCategory, setActiveCategory] = useState<string | null>(null)

    const categories = useMemo(() => {
        const counts = new Map<string, number>()

        for (const project of projects) {
            const category = project.category?.trim()

            if (category) counts.set(category, (counts.get(category) ?? 0) + 1)
        }

        return Array.from(counts, ([label, count]) => ({ label, count }))
    }, [projects])

    const visibleProjects = activeCategory
        ? projects.filter(project => project.category?.trim() === activeCategory)
        : projects

    if (projects.length === 0) {
        return (
            <p className='rounded-2xl border border-dashed border-border px-6 py-16 text-center text-muted-foreground'>
                New projects are on their way. Check back soon.
            </p>
        )
    }

    return (
        <div>
            {categories.length > 1 && (
                <div
                    role='group'
                    aria-label='Filter projects by category'
                    className='flex flex-wrap gap-2'
                >
                    <FilterButton
                        label='All'
                        count={projects.length}
                        active={activeCategory === null}
                        onSelect={() => setActiveCategory(null)}
                    />

                    {categories.map(category => (
                        <FilterButton
                            key={category.label}
                            label={category.label}
                            count={category.count}
                            active={activeCategory === category.label}
                            onSelect={() => setActiveCategory(category.label)}
                        />
                    ))}
                </div>
            )}

            <p className='sr-only' aria-live='polite'>
                {`Showing ${visibleProjects.length} of ${projects.length} projects`}
            </p>

            <div className='mt-10 grid gap-x-8 gap-y-16 sm:mt-12 md:grid-cols-2'>
                {visibleProjects.map((project, index) => (
                    <ProjectCard
                        key={project.id}
                        project={project}
                        preload={index === 0}
                    />
                ))}
            </div>
        </div>
    )
}
